import { useMemo } from "react";
import dayjs from "dayjs";
import {
  useGetMissionControlTenantSiopScheduleQuery,
  SiopScheduleMixGroupPoint,
} from "src/store/api/generatedApi";

export interface ScheduleDataPoint {
  value: number;
  label: string;
}

export interface MixGroupScheduleRow {
  label: string;
  values: Record<string, number>;
}

export const formatScheduleValue = (value: number) =>
  Math.round(value).toLocaleString();

interface SchedulePoint {
  month_start: string;
  scheduled_tons: number;
}

const aggregateSchedule = (allSeries: SchedulePoint[][]): SchedulePoint[] => {
  const byMonth = new Map<string, number>();

  allSeries.flat().forEach((point) => {
    const month = point.month_start;
    byMonth.set(month, (byMonth.get(month) ?? 0) + point.scheduled_tons);
  });

  return [...byMonth.entries()]
    .sort(([monthA], [monthB]) => monthA.localeCompare(monthB))
    .map(([month_start, scheduled_tons]) => ({
      month_start,
      scheduled_tons,
    }));
};

const aggregateMixGroupSeries = (
  allSeries: SiopScheduleMixGroupPoint[][]
): SiopScheduleMixGroupPoint[] => {
  const byKey = new Map<string, number>();

  allSeries.flat().forEach((point) => {
    const key = `${point.month_start}|${point.mix_group_name}`;
    byKey.set(key, (byKey.get(key) ?? 0) + point.scheduled_tons);
  });

  return [...byKey.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, scheduled_tons]) => {
      const separatorIndex = key.indexOf("|");
      return {
        month_start: key.slice(0, separatorIndex),
        mix_group_name: key.slice(separatorIndex + 1),
        scheduled_tons,
      };
    });
};

export const useTenantSchedule = (tenantNames: string[]) => {
  const primaryTenant = tenantNames[0] ?? "";
  const secondaryTenant = tenantNames[1] ?? "";

  const { data: primaryScheduleData, isLoading: primaryLoading } =
    useGetMissionControlTenantSiopScheduleQuery(
      { tenantName: primaryTenant },
      { skip: primaryTenant.length === 0 }
    );
  const { data: secondaryScheduleData, isLoading: secondaryLoading } =
    useGetMissionControlTenantSiopScheduleQuery(
      { tenantName: secondaryTenant },
      { skip: secondaryTenant.length === 0 }
    );

  const isLoading = primaryLoading || secondaryLoading;

  const series = useMemo(() => {
    const allSeries = [
      primaryScheduleData?.series ?? [],
      secondaryScheduleData?.series ?? [],
    ].filter((points) => points.length > 0) as SchedulePoint[][];
    return aggregateSchedule(allSeries);
  }, [primaryScheduleData, secondaryScheduleData]);

  const totalScheduledTons = useMemo(
    () => series.reduce((sum, point) => sum + point.scheduled_tons, 0),
    [series]
  );

  const hasScheduleData = series.length > 0 && totalScheduledTons > 0;

  const firstMonth = useMemo(() => {
    if (!series.length) return null;
    return series[0];
  }, [series]);

  const scheduleDataPoints = useMemo((): ScheduleDataPoint[] => {
    if (!series.length) return [];
    return series.map((s) => ({
      value: s.scheduled_tons,
      label: dayjs(s.month_start).format("MMM"),
    }));
  }, [series]);

  const formattedMonth = useMemo(() => {
    if (!firstMonth) return "";
    return dayjs(firstMonth.month_start).format("MMM YYYY");
  }, [firstMonth]);

  const mixGroupNames = useMemo(() => {
    const primary = primaryScheduleData?.mix_group_names ?? [];
    const secondary = secondaryScheduleData?.mix_group_names ?? [];
    return [...new Set([...primary, ...secondary])];
  }, [primaryScheduleData, secondaryScheduleData]);

  const mixGroupSeries = useMemo(() => {
    const allSeries = [
      primaryScheduleData?.mix_group_series ?? [],
      secondaryScheduleData?.mix_group_series ?? [],
    ].filter((points) => points.length > 0);
    return aggregateMixGroupSeries(allSeries);
  }, [primaryScheduleData, secondaryScheduleData]);

  const mixGroupDataPoints = useMemo((): MixGroupScheduleRow[] => {
    if (!mixGroupSeries.length) return [];

    const byMonth = new Map<string, Record<string, number>>();
    mixGroupSeries.forEach((point) => {
      const label = dayjs(point.month_start).format("MMM");
      const existing = byMonth.get(label) ?? {};
      existing[point.mix_group_name] = point.scheduled_tons;
      byMonth.set(label, existing);
    });

    return [...byMonth.entries()].map(([label, values]) => ({
      label,
      values,
    }));
  }, [mixGroupSeries]);

  return {
    isLoading,
    hasScheduleData,
    firstMonth,
    scheduleDataPoints,
    formattedMonth,
    mixGroupNames,
    mixGroupDataPoints,
  };
};
